import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, BookOpen, CreditCard, Video, Users, Settings, HelpCircle, ChevronRight } from 'lucide-react';
import Header from '../components/Header';

const HelpCenterPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const helpCategories = [
    {
      icon: <Video className="w-8 h-8" />,
      title: "Getting Started",
      description: "Learn how to order your first personalized band video",
      articles: [
        "How to find your school",
        "Choosing the right occasion",
        "Placing your first order",
        "What to expect after ordering"
      ]
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Billing & Payments",
      description: "Everything about pricing, payments, and refunds",
      articles: [
        "Understanding our pricing",
        "Payment methods accepted",
        "Refund policy",
        "Billing questions"
      ]
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "School Partnerships",
      description: "Information for schools interested in partnering",
      articles: [
        "How to become a partner school",
        "Requirements for school bands",
        "Revenue sharing details",
        "Technical requirements"
      ]
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Technical Support",
      description: "Help with video delivery and technical issues",
      articles: [
        "Video delivery timeline",
        "Downloading your video",
        "Video quality issues",
        "Contacting support"
      ]
    }
  ];

  const popularArticles = [
    {
      title: "How long does it take to receive my video?",
      category: "Getting Started",
      readTime: "2 min read"
    },
    {
      title: "What if my school isn't listed?",
      category: "Getting Started",
      readTime: "3 min read"
    },
    {
      title: "Can I request specific songs?",
      category: "Getting Started",
      readTime: "2 min read"
    },
    {
      title: "How do I download my video?",
      category: "Technical Support",
      readTime: "1 min read"
    },
    {
      title: "What's your refund policy?",
      category: "Billing & Payments",
      readTime: "4 min read"
    }
  ];

  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-midnight text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">
            Help Center
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Find answers to your questions and get the support you need
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white text-black border-2 border-black rounded-lg focus:outline-none focus:border-goldie"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Popular Articles Section */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center font-display">Popular Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularArticles.map((article, index) => (
              <div key={index} className="bg-white p-6 rounded-lg border-2 border-black hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-semibold mb-2">{article.title}</h3>
                <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                  <span className="bg-goldie px-2 py-1 rounded text-black font-semibold">
                    {article.category}
                  </span>
                  <span>{article.readTime}</span>
                </div>
                <button className="text-goldie font-semibold hover:underline flex items-center">
                  Read Article
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Help Categories Section */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center font-display">Browse by Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {helpCategories.map((category, index) => (
              <div key={index} className="bg-sky text-black p-8 rounded-lg border-2 border-black">
                <div className="flex items-center mb-6">
                  <div className="bg-goldie p-3 rounded-full mr-4">
                    {category.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-display">{category.title}</h3>
                    <p className="text-gray-700">{category.description}</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {category.articles.map((article, articleIndex) => (
                    <li key={articleIndex}>
                      <button className="text-left w-full hover:text-goldie transition-colors flex items-center justify-between">
                        <span>{article}</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support Section */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white p-8 rounded-lg border-2 border-black">
            <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-black" />
            </div>
            <h2 className="text-3xl font-bold mb-4 font-display">Still Need Help?</h2>
            <p className="text-lg text-gray-700 mb-8">
              Can't find what you're looking for? Our support team is here to help you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-goldie text-black hover:bg-opacity-90 px-8 py-4 border-2 border-black font-bold rounded-lg"
              >
                Contact Support
              </Link>
              <Link
                to="/faq"
                className="bg-transparent text-black border-2 border-black hover:bg-black hover:text-white px-8 py-4 font-bold rounded-lg"
              >
                View All FAQs
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center font-display">Quick Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <BookOpen className="w-12 h-12 text-goldie mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">User Guide</h3>
              <p className="text-gray-300 mb-4">
                Step-by-step instructions for using BandCam.io
              </p>
              <button className="text-goldie font-semibold hover:underline">
                Read Guide
              </button>
            </div>
            <div className="text-center">
              <Video className="w-12 h-12 text-goldie mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Video Tutorials</h3>
              <p className="text-gray-300 mb-4">
                Watch helpful videos about our platform
              </p>
              <button className="text-goldie font-semibold hover:underline">
                Watch Videos
              </button>
            </div>
            <div className="text-center">
              <Users className="w-12 h-12 text-goldie mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Community</h3>
              <p className="text-gray-300 mb-4">
                Connect with other users and share experiences
              </p>
              <button className="text-goldie font-semibold hover:underline">
                Join Community
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight text-white border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="BandCam.io Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-2 text-xl font-bold">BandCam.io</span>
              </div>
              <p className="text-gray-300 mb-4">
                Connecting families with their school bands to create unforgettable personalized video messages for special occasions.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/schools" className="text-gray-300 hover:text-white">Find Your School</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
                <li><Link to="/faq" className="text-gray-300 hover:text-white">FAQ</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BandCam.io. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenterPage;
